import { Product } from "@/app/page";
import React, { createContext, useContext, useEffect, useState } from "react";

export type WidgetAPI = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  products: Product[];
  addProduct: (product: Product) => void;
  removeProduct: (productId: string) => void;
  removeAllProducts: () => void;
  step: number;
  setStep: (step: number) => void;
  trialRequestId: string | null;
  setTrialRequestId: (trialRequestId: string | null) => void;
};

const WidgetContext = createContext<WidgetAPI | null>(null);

export const useWidget = () => useContext(WidgetContext)!;

export const WidgetProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Add state management inside the context
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<number>(1);
  const [trialRequestId, setTrialRequestId] = useState<string | null>(null);
  const [products, setProducts] = useState<Product[]>(() => {
    // Initialize products from localStorage if available
    if (typeof window !== "undefined") {
      const savedProducts = localStorage.getItem("fhtProducts");
      return savedProducts ? JSON.parse(savedProducts) : [];
    }
    return [];
  });

  // Sync products with localStorage whenever they change
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("fhtProducts", JSON.stringify(products));
    }
  }, [products]);

  const addProduct = (product: Product) => {
    setStep(1);
    setProducts((prevProducts) => {
      // Check if product already exists
      const exists = prevProducts.some((p) => p.id === product.id);
      if (exists) {
        return prevProducts; // Don't add if already exists
      }
      setIsOpen(true);
      return [...prevProducts, product];
    });
  };

  const removeProduct = (productId: string) => {
    setProducts((prevProducts) =>
      prevProducts.filter((product) => product.id !== productId),
    );

    // get the class name "added" from the page using the attribute data-variant-id
    const productElement = document.querySelector(
      `[data-variant-id="${productId}"]`,
    ) as HTMLElement;
    if (productElement) {
      productElement.classList.remove("added");
    }
  };

  const removeAllProducts = () => {
    setProducts([]);
    if (typeof window !== "undefined") {
      localStorage.removeItem("fhtProducts");
    }
  };

  const api: WidgetAPI = {
    isOpen,
    open: () => {
      setIsOpen(true);
      // setStep(1);
    },
    close: () => {
      setIsOpen(false);
      // setStep(1);
    },
    products,
    addProduct,
    removeProduct,
    removeAllProducts,
    step,
    setStep,
    trialRequestId,
    setTrialRequestId,
  };

  // Expose to window for external scripts
  useEffect(() => {
    (window as any).wesenceTBYB = api;
    return () => {
      delete (window as any).wesenceTBYB;
    };
  }, [isOpen, products]);

  return (
    <WidgetContext.Provider value={api}>{children}</WidgetContext.Provider>
  );
};
